import React, { useState, useEffect } from 'react';
import { securePut, secureGet, securePost } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { formatAmount } from "../../utils/format";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';
import GeneratePopup from "../../components/GeneratePopUp";
import { PAYMENT_ENUMS } from "../../utils/paymentModes";

import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import plusY from '../../icons/plusY.svg';
import minusY from '../../icons/minusY.svg';
import noImage from '../../icons/noImageIcon.svg';
import EyeIcon from '../../icons/eyeB.svg';
import xIcon from '../../icons/x.svg';

const QuoteForm = ({
    title,
    baseFields = [],
    listFields = [],
    products = [],
    services = [],
    typeOptions = [],
    onSuccess,
    onCancel,
    initialValues = {},
    onRevisionChange, // Callback to expose revision change handler
}) => {
    const [formValues, setFormValues] = useState({});
    const [listEntities, setListEntities] = useState([]);
    const [showReasonSelector, setShowReasonSelector] = useState(null);
    const [currentEntity, setCurrentEntity] = useState({});
    const [itemJustifications, setItemJustifications] = useState({}); // Store justifications per productId (for bon de retour items)
    const [pendingAction, setPendingAction] = useState(null); // Store pending action data for cancellation
    const [bonDeRetourItems, setBonDeRetourItems] = useState([]); // Store items removed/reduced for bon de retour
    const [originalProductDetails, setOriginalProductDetails] = useState({}); // Store original product details by productId for restoration
    const [totalHT, setTotalHT] = useState(0);
    const [totalTTC, setTotalTTC] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [originalQuoteDetails, setOriginalQuoteDetails] = useState([]); // Store original quote details
    const [hasModifications, setHasModifications] = useState(false); // Track if any modifications were made
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const id = location.state?.id;
    const [selectedRevisionId, setSelectedRevisionId] = useState(location.state?.revisionId || initialValues?.revisionId || null);
    const [quote, setQuote] = useState(null);

    // Expose revision change handler to parent component
    useEffect(() => {
        if (onRevisionChange) {
            onRevisionChange((revisionId) => {
                setSelectedRevisionId(revisionId);
            });
        }
    }, [onRevisionChange]);

    // Listen for revisionId changes from location.state
    useEffect(() => {
        const revisionId = location.state?.revisionId || initialValues?.revisionId || null;
        if (revisionId !== selectedRevisionId) {
            setSelectedRevisionId(revisionId);
        }
    }, [location.state?.revisionId, initialValues?.revisionId]);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    useEffect(() => {
        const checkDataReadiness = () => {
            const baseFieldsReady = baseFields.every(field =>
                !field.options || field.options.length > 0
            );

            if (id) {
                return quote && baseFieldsReady;
            }
            return baseFieldsReady;
        };

        if (checkDataReadiness()) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [quote, baseFields, id]);

    useEffect(() => {
        // If initialValues contains a quote object (has id and quote-specific fields), use it directly
        if (initialValues && initialValues.id && (initialValues.details !== undefined || initialValues.client !== undefined || initialValues.quoteNumber !== undefined)) {
            setQuote(initialValues);
            return;
        }

        // Otherwise, fetch quote using id from location.state
        const abortController = new AbortController();
        const fetchQuote = async () => {
            if (!id) return;
            try {
                // Build URL with revisionId if provided
                let url = `/quotes/${id}`;
                if (selectedRevisionId) {
                    url += `?revisionId=${selectedRevisionId}`;
                }
                const response = await secureGet(url, showSnackbar, { signal: abortController.signal });
                setQuote(response.data);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Failed to fetch quote:", error);
                }
            }
        };

        fetchQuote();
        return () => {
            abortController.abort();
        };
    }, [id, initialValues, selectedRevisionId]);

    useEffect(() => {
        if (quote) {
            // Store clientId from initialValues for payload (always prefer initialValues.clientId)
            // Store clientName from initialValues for display in text field
            const formInitialValues = {
                clientId: initialValues?.clientId || quote.client?.id,
                clientName: initialValues?.clientName || quote.client?.name || "",
                repoId: quote.repo?.id || "",
                comment: quote.comment || "",
                paymentMode: quote.paymentMode || "",
                advancePayment: quote.advancePayment || 0,
            };
            setFormValues(formInitialValues);

            const productList = quote.details?.map((detail) => ({
                detailId: detail.id,
                product: detail.product,
                quantity: detail.quantity,
                discount: detail.salesDiscount || 0,
            })) || [];
            setListEntities(productList);
            // Store original details for comparison (including detailId for removed items)
            setOriginalQuoteDetails(productList.map(item => ({
                detailId: item.detailId,
                productId: item.product?.id,
                quantity: item.quantity,
            })));
            // Store full original product details for restoration
            const originalDetailsMap = {};
            productList.forEach(item => {
                if (item.product?.id) {
                    originalDetailsMap[item.product.id] = {
                        product: item.product,
                        quantity: item.quantity,
                        discount: item.discount || 0,
                    };
                }
            });
            setOriginalProductDetails(originalDetailsMap);
            setHasModifications(false);
        } else if (initialValues && initialValues.receptionDocumentId && !quote) {
            // For new quotes, set receptionDocumentId, clientId (for payload), and clientName (for display)
            setFormValues((prev) => ({
                ...prev,
                receptionDocumentId: initialValues.receptionDocumentId,
                clientId: initialValues.clientId || prev.clientId, // For payload
                clientName: initialValues.clientName || prev.clientName || "", // For display
            }));
        } else if (initialValues?.clientId && !quote) {
            // Always pre-fill clientId (for payload) and clientName (for display) from initialValues
            setFormValues((prev) => ({
                ...prev,
                clientId: initialValues.clientId, // For payload
                clientName: initialValues.clientName || "", // For display
            }));
        }
    }, [quote, initialValues]);

    // Track modifications when listEntities changes
    useEffect(() => {
        if (isModifyModeWithSignedFiche() && originalQuoteDetails.length > 0) {
            const hasChanges = checkForModifications(listEntities);
            setHasModifications(hasChanges);
        } else if (!isModifyModeWithSignedFiche()) {
            setHasModifications(false);
        }
    }, [listEntities, originalQuoteDetails, quote, initialValues]);

    useEffect(() => {
        const calculatedTotalHT = listEntities.reduce(
            (sum, entity) =>
                sum +
                entity.quantity * (entity.product.priceHT * (1 - entity.discount / 100)),
            0
        );

        const calculatedTotalTTC = listEntities.reduce(
            (sum, entity) =>
                sum +
                entity.quantity * (entity.product.priceTTC * (1 - entity.discount / 100)),
            0
        );

        setTotalHT(calculatedTotalHT.toFixed(2));
        setTotalTTC(calculatedTotalTTC.toFixed(2));
    }, [listEntities]);

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const removeListEntity = (index) => {
        setListEntities((prevEntities) =>
            prevEntities.filter((_, i) => i !== index)
        );
    };

    const isModifyModeWithSignedFiche = () => {
        // Check both quote.ficheReception?.status and initialValues (when passed from FicheReceptionDetails)
        const ficheStatus = initialValues?.frStatus;
        console.log("ficheStatus", ficheStatus);
        return (id || initialValues?.id) && ficheStatus === "SIGNED";
    };

    // Check if an entity is a service (not a product)
    const isService = (entity) => {
        console.log("entity", entity);
        return entity?.product?.family?.familyType === "SERVICE";
    };

    // Check if there are any modifications that require justification
    // Only deletions and quantity reductions require justification, NOT additions
    // Services are excluded from bon de retour logic
    const checkForModifications = (currentEntities) => {
        if (!isModifyModeWithSignedFiche() || originalQuoteDetails.length === 0) {
            return false;
        }

        // Check if any original item was deleted (excluding services)
        for (const originalDetail of originalQuoteDetails) {
            // Check if the original item was a service using originalProductDetails
            const originalProductDetail = originalProductDetails[originalDetail.productId];
            // Skip services - they don't require bon de retour
            if (originalProductDetail && isService({ product: originalProductDetail.product })) {
                continue;
            }
            
            const currentEntity = currentEntities.find(
                curr => curr.product?.id === originalDetail.productId
            );
            if (!currentEntity) {
                // Original item was deleted - requires justification (only for products)
                return true;
            }
        }

        // Check if any quantity was reduced (not increased) - excluding services
        for (const currentEntity of currentEntities) {
            // Skip services - they don't require bon de retour
            if (isService(currentEntity)) {
                continue;
            }
            
            const originalDetail = originalQuoteDetails.find(
                orig => orig.productId === currentEntity.product?.id
            );
            if (originalDetail && originalDetail.quantity > currentEntity.quantity) {
                // Quantity was reduced - requires justification (only for products)
                return true;
            }
        }

        // Adding new items or increasing quantities does NOT require justification
        return false;
    };

    const handleTrashClick = (index) => {
        // Check if this is a newly added item (not in original quote)
        const entity = listEntities[index];
        const isNewlyAdded = !originalQuoteDetails.find(
            orig => orig.productId === entity.product?.id
        );
        
        // If it's a service, always remove without bon de retour logic
        if (isService(entity)) {
            removeListEntity(index);
            return;
        }
        
        if (isNewlyAdded) {
            // For newly added items, just remove without prompting
            removeListEntity(index);
        } else if (isModifyModeWithSignedFiche()) {
            // Store the item to be deleted for potential cancellation
            const itemToDelete = listEntities[index];
            setPendingAction({ index, type: 'deletion', item: itemToDelete });
            // Show reason selector for this item
            setShowReasonSelector({ index, type: 'deletion' });
        } else {
            // Normal delete in add mode or unsigned fiche
            removeListEntity(index);
        }
    };

    const handleReasonSelect = (reasonSelectorData, reason) => {
        const { index, type, quantityDifference } = reasonSelectorData || {};
        const reasonText = reason === "CLIENT" ? "DEMANDE DU CLIENT" : "DÉCISION DU CHEF D'ATELIER";
        
        if (type === 'deletion') {
            // Add item to bon de retour (merge if same product exists)
        const itemToMove = listEntities[index];
            const productId = itemToMove.product?.id;
            
            // Store justification by productId (store both enum value and display text)
            if (productId) {
                setItemJustifications((prev) => ({
                    ...prev,
                    [productId]: {
                        enumValue: reason, // Store enum value for payload
                        displayText: reasonText // Store display text for UI
                    }
                }));
            }
            
            setBonDeRetourItems((prev) => {
                const existingIndex = prev.findIndex(item => item.product?.id === productId);
                if (existingIndex >= 0) {
                    // Merge with existing item - increase quantity
                    const newItems = [...prev];
                    newItems[existingIndex] = {
                        ...newItems[existingIndex],
                        quantity: newItems[existingIndex].quantity + itemToMove.quantity,
                        returnReason: reasonText // Update reason
                    };
                    return newItems;
                } else {
                    // Add new item
                    return [
            ...prev,
            {
                ...itemToMove,
                reason: reason,
                            returnReason: reasonText
            }
                    ];
                }
            });
        
        // Remove from main list
        removeListEntity(index);
        } else if (type === 'quantity_reduction') {
            // Add reduced quantity to bon de retour (merge if same product exists)
            const entity = listEntities[index];
            const productId = entity.product?.id;
            
            // Store justification by productId (store both enum value and display text)
            if (productId) {
                setItemJustifications((prev) => ({
                    ...prev,
                    [productId]: {
                        enumValue: reason, // Store enum value for payload
                        displayText: reasonText // Store display text for UI
                    }
                }));
            }
            
            const returnItem = {
                ...entity,
                quantity: quantityDifference,
                reason: reason,
                returnReason: reasonText
            };
            
            setBonDeRetourItems((prev) => {
                const existingIndex = prev.findIndex(item => item.product?.id === productId);
                if (existingIndex >= 0) {
                    // Merge with existing item - increase quantity
                    const newItems = [...prev];
                    newItems[existingIndex] = {
                        ...newItems[existingIndex],
                        quantity: newItems[existingIndex].quantity + quantityDifference,
                        returnReason: reasonText // Update reason
                    };
                    return newItems;
                } else {
                    // Add new item
                    return [...prev, returnItem];
                }
            });
            
            // Update the current entity quantity
            const newEntities = [...listEntities];
            newEntities[index] = {
                ...newEntities[index],
                quantity: reasonSelectorData.currentQuantity
            };
            setListEntities(newEntities);
        }
        // No need to handle 'addition' type anymore - new items don't require justification
        
        // Close reason selector and clear pending action
        setShowReasonSelector(null);
        setPendingAction(null);
    };

    const handleCancelAction = () => {
        if (!pendingAction) {
            setShowReasonSelector(null);
            return;
        }

        const { index, type } = pendingAction;

        if (type === 'deletion') {
            // Item hasn't been removed yet, just close the selector
            // No action needed - item remains in the list
        } else if (type === 'quantity_reduction') {
            // Restore original quantity
            const originalQuantity = pendingAction.originalQuantity;
            const newEntities = [...listEntities];
            newEntities[index] = {
                ...newEntities[index],
                quantity: originalQuantity
            };
            setListEntities(newEntities);
        }

        // Clear pending action and close selector
        setPendingAction(null);
        setShowReasonSelector(null);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // In modify mode, listFields are not required if listEntities already exist
        const fieldsToValidate = listEntities.length > 0 
            ? [...baseFields] 
            : [...baseFields, ...listFields];
        
        // Check if we need justification (modify mode with signed fiche and modifications detected)
        const needsJustification = isModifyModeWithSignedFiche() && hasModifications;
        
        // Validate that reasons were selected for all bon de retour items
        if (needsJustification) {
            // If modifications exist, there should be bon de retour items
            if (bonDeRetourItems.length === 0) {
                showSnackbar(`Veuillez remplir les champs obligatoires : Justification`, 'error');
                return;
            }
            
            // Check if all bon de retour items have justifications
            const missingJustifications = bonDeRetourItems.filter(item => {
                const productId = item.product?.id;
                if (!productId) return true;
                const justification = itemJustifications[productId];
                // Check if justification exists and has the required structure
                const isMissing = !justification || !justification.enumValue || !justification.displayText;
                return isMissing;
            });
            
            if (missingJustifications.length > 0) {
                showSnackbar(`Veuillez remplir les champs obligatoires : Justification`, 'error');
                return;
            }
            
        }
            
        const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }
        
        // Build payload according to backend format
        // First, map current entities
        const currentDetails = listEntities.map((entity, index) => {
            const detail = {
                id: entity.detailId ? parseInt(entity.detailId, 10) : 0,
                productId: parseInt(entity.product?.id, 10) || 0,
                quantity: parseInt(entity.quantity, 10) || 0,
                salesDiscount: parseFloat(entity.discount || 0),
            };
            // Add changeRequestedBy if this product has a justification (was modified)
            const productId = entity.product?.id;
            if (productId && itemJustifications[productId]) {
                // Map enum value: "CLIENT" -> "CLIENT", "WORKSHOP" -> "WORKSHOP_MANAGER"
                const enumValue = itemJustifications[productId].enumValue;
                detail.changeRequestedBy = enumValue === "CLIENT" ? "CLIENT" : "WORKSHOP_MANAGER";
            }
            return detail;
        }).filter(detail => detail.productId > 0); // Filter out invalid details

        // Find removed items (items in originalQuoteDetails but not in current listEntities)
        const removedDetails = [];
        if (quote?.id && originalQuoteDetails.length > 0) {
            originalQuoteDetails.forEach(originalDetail => {
                const stillExists = listEntities.find(
                    entity => entity.product?.id === originalDetail.productId
                );
                if (!stillExists && originalDetail.detailId) {
                    // Item was removed, add it with quantity 0
                    const removedDetail = {
                        id: parseInt(originalDetail.detailId, 10),
                        productId: parseInt(originalDetail.productId, 10) || 0,
                        quantity: 0,
                        salesDiscount: 0,
                    };
                    // Add changeRequestedBy if this product has a justification
                    if (itemJustifications[originalDetail.productId]) {
                        const enumValue = itemJustifications[originalDetail.productId].enumValue;
                        removedDetail.changeRequestedBy = enumValue === "CLIENT" ? "CLIENT" : "WORKSHOP_MANAGER";
                    }
                    removedDetails.push(removedDetail);
                }
            });
        }

        const payload = {
            clientId: parseInt(formValues.clientId, 10) || 0,
            repoId: parseInt(formValues.repoId, 10) || 0,
            advancePayment: parseFloat(formValues.advancePayment || 0),
            ...(initialValues?.receptionDocumentId && { receptionDocumentId: parseInt(initialValues.receptionDocumentId, 10) }),
            ...(quote?.id && { quoteId: parseInt(quote.id, 10) }),
            details: [...currentDetails, ...removedDetails],
        };


        try {
            setSubmitting(true);
            console.log("Submitting quote with payload:", payload);
            await securePut("/quotes/add", payload, showSnackbar);
            onSuccess();
            setFormValues({});
            setListEntities([]);
        } catch (error) {
            console.error("Failed to submit quote:", error);
        } finally {
            setSubmitting(false);
        }
    };



    const addListEntity = () => {
        const { type, product, quantity, advancePayment } = currentEntity;
        if (!type || !product || !quantity || quantity <= 0) {
            showSnackbar(t('placeholder.empty_fields'), "error");
            return;
        }

        // Find the selected product/service based on type
        const productList = type === 'services' ? services : products;
        const selectedItem = productList.find((p) => p.value === product);
        
        if (!selectedItem) {
            showSnackbar('Produit ou service introuvable', "error");
            return;
        }

        const productId = selectedItem.fullEntity?.id;
        const newQuantity = parseInt(quantity, 10);

        // Check if the product already exists in the list
        const existingIndex = listEntities.findIndex(
            (entity) => entity.product?.id === productId
        );

        if (existingIndex >= 0) {
            // Product already exists, update quantity by adding new quantity to existing quantity
            const existingEntity = listEntities[existingIndex];
            const updatedQuantity = existingEntity.quantity + newQuantity;

            setListEntities((prev) => {
                const newEntities = [...prev];
                newEntities[existingIndex] = {
                    ...existingEntity,
                    quantity: updatedQuantity,
                };
                return newEntities;
            });
        } else {
            // Product doesn't exist, add as new entity
        const newEntity = {
            product: selectedItem.fullEntity,
                quantity: newQuantity,
            discount: 0,
        };

        setListEntities((prev) => [...prev, newEntity]);
        }

        setCurrentEntity({});
        
        // No justification prompt for new items - they can be added freely
    };


    const handleImageError = (e) => {
        e.target.src = noImage;
        e.target.style.width = "25px";
        e.target.style.height = "25px";
        e.target.style.objectFit = "contain";
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
            <div className="bg-white rounded-[12px] shadow-lg w-[75%] h-[760px] flex flex-col">
                <Helmet>
                    <title>Drivnest - {t("titles.quotes")}</title>
                </Helmet>

                {/* Header with title and close button */}
                <div className="p-6 pb-0">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
                    </div>
                </div>
                
                {/* Content */}
                <div className="px-8 overflow-hidden flex-1 flex flex-col">
                    {(
                <form className="flex flex-col h-full">
                    {/* Base Fields - Fixed */}
                    <div className="mb-4">
                        <div className="grid grid-cols-3 gap-4">
                            {baseFields.map((field, index) => {
                                // For clientId field (text field), display clientName but payload uses clientId
                                let displayValue = formValues[field.name] || "";
                                if (field.name === 'clientId') {
                                    // Display clientName from initialValues or formValues
                                    displayValue = formValues.clientName || initialValues?.clientName || "";
                                }
                                
                                return (
                                    <div key={index}>
                                        <Input
                                            type={field.type || "text"}
                                            label={field.label}
                                            name={field.name}
                                            value={displayValue}
                                            options={field.options || []}
                                            required={field.required}
                                                    disabled={field.name === 'clientId'}
                                            onChange={(value) => {
                                                if (field.name === 'clientId') {
                                                    // Update clientName for display, but keep clientId from initialValues for payload
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        clientName: value,
                                                        // Always preserve clientId from initialValues for payload
                                                        clientId: initialValues?.clientId || prev.clientId,
                                                    }));
                                                } else {
                                                    handleInputChange(field.name, value);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {errorMessage && (
                            <p className="mt-2 text-sm text-red-500 font-semibold">{errorMessage}</p>
                        )}
                    </div>

                    {/* List Fields - Fixed */}
                    {listFields.length > 0 && (
                        <div className="mb-4">
                            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.products`)}</h3>
                                    <div className="flex items-end gap-3 mt-2">
                                        <div className="grid grid-cols-3 gap-4 flex-1">
                                {listFields.map((field, index) => {
                                    let fieldLabel = field.label;
                                    if (field.name === 'product') {
                                        fieldLabel = currentEntity.type === 'services' 
                                            ? t(`fields.list.service`) 
                                            : t(`fields.list.product`);
                                    }
                                    
                                    return (
                                        <Input
                                            key={index}
                                            type={field.type || 'text'}
                                            label={fieldLabel}
                                            name={field.name}
                                            value={currentEntity[field.name] || ''}
                                            required={field.required}
                                            onChange={(value) => {
                                                if (field.name === 'type') {
                                                    setCurrentEntity({
                                                        ...currentEntity,
                                                        type: value,
                                                        product: '',
                                                    });
                                                } else {
                                                    setCurrentEntity((prev) => ({ ...prev, [field.name]: value }));
                                                }
                                            }}
                                            options={field.name === 'product'
                                                ? (currentEntity.type === 'services' ? services : products).map((item) => ({
                                                    value: item.value,
                                                    name: item.name,
                                                    reference: item.reference,
                                                    fullEntity: item.fullEntity,
                                                }))
                                                : field.name === 'type'
                                                ? typeOptions
                                                : field.options || []}
                                        />
                                    );
                                })}
                                        </div>
                                <button
                                    type="button"
                                    onClick={addListEntity}
                                    className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                                >
                                    <img src={plusIcon} alt="Add" className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* List Entity List - Scrollable */}
                    {listEntities.length > 0  && (
                        <div className="flex-1 overflow-y-auto space-y-[8px]">
                            {listEntities.map((entity, index) => {
                                const discountMultiplier = 1 - (entity.discount || 0) / 100;
                                const discountedPriceHT =
                                    entity.discount > 0
                                        ? (entity.product.priceHT * discountMultiplier).toFixed(2)
                                        : entity.product.priceHT?.toFixed(2);
                                const discountedPriceTTC =
                                    entity.discount > 0
                                        ? (entity.product.priceTTC * discountMultiplier).toFixed(2)
                                        : entity.product.priceTTC?.toFixed(2);

                                return (
                                    <div
                                        key={index}
                                        className="grid grid-cols-[25%_15%_15%_15%_15%_15%] items-center py-2 px-4 rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-0"
                                    >
                                        {/* Product Info */}
                                        <div className="flex items-center gap-2">
                                            <div className="w-[76px] h-[76px] flex items-center justify-center  shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-[12px] overflow-hidden">
                                                {entity.product?.image ? (
                                                    <img
                                                        src={entity.product.image}
                                                        alt={entity.product.name}
                                                        className="w-full h-full object-contain"
                                                        onError={handleImageError}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-white"></div>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-[#043F88] font-semibold text-[12px]">
                                                    {entity.product?.reference || "N/A"}
                                                </p>
                                                <p className="text-[#6B78F8] font-semibold text-[16px]">
                                                    {entity.product?.name || "Produit"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Prix HT */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuHT`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceHT)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.product?.priceHT)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Prix TTC */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuTTC`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceTTC)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.product?.priceTTC)}
                                                </p>
                                            )}
                                        </div>


                                        {/* Quantité */}
                                        <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.quantity`).toUpperCase()}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6C78F858] divide-x divide-[#6C78F8]">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const currentQuantity = listEntities[index].quantity;
                                                        const newQuantity = Math.max(1, currentQuantity - 1);
                                                        
                                                        // If it's a service, always reduce quantity without bon de retour logic
                                                        if (isService(entity)) {
                                                            const newEntities = [...listEntities];
                                                            newEntities[index] = {
                                                                ...newEntities[index],
                                                                quantity: newQuantity
                                                            };
                                                            setListEntities(newEntities);
                                                            return;
                                                        }
                                                        
                                                        // Check if quantity is being reduced and fiche is signed
                                                        if (isModifyModeWithSignedFiche() && newQuantity < currentQuantity) {
                                                            const originalDetail = originalQuoteDetails.find(
                                                                orig => orig.productId === entity.product?.id
                                                            );
                                                            
                                                            // If reducing below original quantity, show reason selector
                                                            if (originalDetail && newQuantity < originalDetail.quantity) {
                                                                const quantityDifference = originalDetail.quantity - newQuantity;
                                                                // Store original quantity for potential cancellation
                                                                setPendingAction({ index, type: 'quantity_reduction', originalQuantity: currentQuantity });
                                                                // Store the quantity difference for bon de retour
                                                                setShowReasonSelector({ index, type: 'quantity_reduction', quantityDifference, currentQuantity: newQuantity });
                                                            } else {
                                                                // Just reduce quantity normally
                                                        const newEntities = [...listEntities];
                                                        newEntities[index] = {
                                                            ...newEntities[index],
                                                                    quantity: newQuantity
                                                        };
                                                        setListEntities(newEntities);
                                                            }
                                                        } else {
                                                            // Normal quantity decrease
                                                            const newEntities = [...listEntities];
                                                            newEntities[index] = {
                                                                ...newEntities[index],
                                                                quantity: newQuantity
                                                            };
                                                            setListEntities(newEntities);
                                                        }
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={minus} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                                                    {entity.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newEntities = [...listEntities];
                                                        newEntities[index] = {
                                                            ...newEntities[index],
                                                            quantity: newEntities[index].quantity + 1
                                                        };
                                                        setListEntities(newEntities);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={plus} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>


                                        {/* Prix Total */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.totalPrice`)}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(entity.quantity * discountedPriceTTC)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.quantity * entity.product?.priceTTC)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Delete Icon */}
                                        <div className="flex justify-center">
                                            <button
                                                type="button"
                                                onClick={() => handleTrashClick(index)}
                                                className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                            >
                                                <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Reason Selector Banner */}
                                        {showReasonSelector && showReasonSelector.index === index && (
                                                    <div className="col-span-6 mt-1">
                                                        <div className="bg-[#FED4DD] rounded-[12px] p-2">
                                                            <div className="flex items-center justify-between gap-4">
                                                                <p className="text-[#EF466F] font-semibold text-[14px] flex-1">
                                                        {showReasonSelector.type === 'quantity_reduction' 
                                                            ? `La quantité réduite sera ajoutée à un avoir - veuillez sélectionner la raison de l'avoir:`
                                                                        : `Cette ligne sera convertie en avoir - veuillez sélectionner la raison de l'avoir:`
                                                        }
                                                    </p>
                                                                <div className="flex gap-2 flex-shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleReasonSelect(showReasonSelector, "CLIENT")}
                                                                        className="px-6 py-1  bg-[#EF466F] text-white font-semibold text-[12px] rounded-[12px] hover:opacity-90 whitespace-nowrap"
                                                        >
                                                            Demande du client
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleReasonSelect(showReasonSelector, "WORKSHOP")}
                                                                        className="px-6 py-1 bg-[#EF466F] text-white font-semibold text-[12px] rounded-[12px] hover:opacity-90 whitespace-nowrap"
                                                        >
                                                            Décision du chef d'atelier
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleCancelAction}
                                                                        className="px-6 py-1 bg-white text-[#EF466F] font-semibold text-[12px] rounded-[12px] border border-[#EF466F] hover:bg-[#FFF5F7] whitespace-nowrap"
                                                        >
                                                            Annuler
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                        </div>
                                                )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}


                </form>
            )}
                </div>

                {/* Totals and Footer */}
                <div className="pl-6 pr-6 pt-0 pb-6 ">
                    <div className="flex justify-between items-center">
                        <div className="flex text-left">
                            <p className="text-[#231E29] text-[16px] font-medium">
                                ACOMPTE:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(formValues.advancePayment)}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL HT:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalHT)}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL TTC:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalTTC)}
                            </p>
                        </div>
                        <div className="space-x-4">
                            <SaveCancel
                                onSaveClick={handleSubmit}
                                onCancelClick={onCancel}
                                loading={submitting}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );
};

export default QuoteForm;